import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  collection, addDoc, query, orderBy, onSnapshot, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const BodyWeightTracker = ({ onBack }) => {
  const [entries, setEntries] = useState([]);
  const [weight, setWeight] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const uid = auth.currentUser?.uid;

  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'users', uid, 'bodyweight'), orderBy('date', 'asc'));
    return onSnapshot(q, (snap) => {
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [uid]);

  const handleAdd = async () => {
    if (!weight || isNaN(weight)) return;
    setLoading(true);
    await addDoc(collection(db, 'users', uid, 'bodyweight'), {
      weight: parseFloat(weight),
      note,
      date: serverTimestamp(),
      dateString: new Date().toLocaleDateString('no-NO', { day: '2-digit', month: 'short' })
    });
    setWeight('');
    setNote('');
    setLoading(false);
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, 'users', uid, 'bodyweight', id));
  };

  const chartData = entries.map(e => ({ date: e.dateString || '', weight: e.weight }));
  const latest = entries[entries.length - 1];
  const first = entries[0];
  const diff = latest && first && entries.length > 1
    ? (latest.weight - first.weight).toFixed(1) : null;

  return (
    <div style={{ padding: '20px', maxWidth: '640px', margin: '0 auto', paddingBottom: '80px' }}>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', color: '#a0aec0',
        fontSize: '14px', cursor: 'pointer', marginBottom: '16px', padding: 0
      }}>← Back</button>

      <h2 style={{ color: 'white', marginBottom: '20px', fontSize: '24px' }}>⚖️ Body Weight</h2>

      {latest && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          <div style={{ flex: 1, background: '#1e2a3a', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
            <div style={{ color: '#63b3ed', fontSize: '24px', fontWeight: 'bold' }}>{latest.weight} kg</div>
            <div style={{ color: '#718096', fontSize: '11px', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Latest</div>
          </div>
          {diff !== null && (
            <div style={{ flex: 1, background: '#1e2a3a', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <div style={{ color: parseFloat(diff) <= 0 ? '#68d391' : '#fc8181', fontSize: '24px', fontWeight: 'bold' }}>
                {parseFloat(diff) > 0 ? '+' : ''}{diff} kg
              </div>
              <div style={{ color: '#718096', fontSize: '11px', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Change</div>
            </div>
          )}
        </div>
      )}

      {chartData.length > 1 && (
        <div style={{ background: '#1e2a3a', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d3748" />
              <XAxis dataKey="date" tick={{ fill: '#718096', fontSize: 11 }} />
              <YAxis domain={['auto', 'auto']} tick={{ fill: '#718096', fontSize: 11 }} width={38} />
              <Tooltip contentStyle={{ background: '#2d3748', border: 'none', borderRadius: '8px', color: 'white' }} />
              <Line type="monotone" dataKey="weight" stroke="#f6993f" strokeWidth={2.5}
                dot={{ fill: '#f6993f', r: 4 }} activeDot={{ r: 7 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ background: '#1e2a3a', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
        <div style={{ color: '#a0aec0', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
          Log Today's Weight
        </div>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <input
            type="number" step="0.1" placeholder="e.g. 82.5"
            value={weight} onChange={e => setWeight(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            style={{
              flex: 1, background: '#2d3748', border: '1px solid #4a5568',
              borderRadius: '8px', padding: '11px 14px', color: 'white',
              fontSize: '16px', outline: 'none'
            }}
          />
          <button onClick={handleAdd} disabled={loading} style={{
            background: 'linear-gradient(135deg, #f6993f, #ed8936)',
            border: 'none', borderRadius: '8px', padding: '11px 22px',
            color: 'white', fontWeight: '700', fontSize: '15px', cursor: 'pointer'
          }}>Log</button>
        </div>
        <input
          type="text" placeholder="Note (optional)"
          value={note} onChange={e => setNote(e.target.value)}
          style={{
            width: '100%', background: '#2d3748', border: '1px solid #4a5568',
            borderRadius: '8px', padding: '10px 14px', color: 'white',
            fontSize: '14px', outline: 'none', boxSizing: 'border-box'
          }}
        />
      </div>

      <div style={{ color: '#a0aec0', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
        History
      </div>

      {[...entries].reverse().map(entry => (
        <div key={entry.id} style={{
          background: '#1e2a3a', borderRadius: '10px', padding: '14px 16px',
          marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <span style={{ color: 'white', fontWeight: '600', fontSize: '17px' }}>{entry.weight} kg</span>
            {entry.note && <span style={{ color: '#718096', fontSize: '13px', marginLeft: '10px' }}>{entry.note}</span>}
            <div style={{ color: '#718096', fontSize: '12px', marginTop: '2px' }}>{entry.dateString}</div>
          </div>
          <button onClick={() => handleDelete(entry.id)} style={{
            background: 'none', border: 'none', color: '#fc8181',
            fontSize: '22px', cursor: 'pointer', lineHeight: 1, padding: '4px 8px'
          }}>×</button>
        </div>
      ))}

      {entries.length === 0 && (
        <div style={{ color: '#4a5568', textAlign: 'center', padding: '40px 20px', fontSize: '14px' }}>
          No entries yet. Log your first weight above!
        </div>
      )}
    </div>
  );
};

export default BodyWeightTracker;